import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { documentsApi, organisationsApi, mergeDocumentNames } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Trash2, Eye, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatFileSize } from '../lib/utils';
import { DocumentViewer } from '../components/DocumentViewer';
import AppShell from '../components/AppShell';
import { PageHeader } from '../components/PageHeader';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import { AnalyseDocumentDialog } from '../components/AnalyseDocumentDialog';
import { AlreadyAnalysedDialog } from '../components/AlreadyAnalysedDialog';
import { StatusPill } from '../components/StatusPills';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const DOCUMENT_TYPES = [
  { id: 'care_notes', label: 'Care notes', description: 'Social worker notes, assessments' },
  { id: 'medical', label: 'Medical records', description: 'GP, hospital records' },
  { id: 'education', label: 'Education records', description: 'School reports, SEN documents' },
  { id: 'id_proof', label: 'Proof of ID', description: 'Passport, driving licence' },
  { id: 'address_proof', label: 'Proof of address', description: 'Utility bill, bank statement' },
  { id: 'other', label: 'Other', description: 'Any other documents' },
];

/**
 * Small chip rendered on every document card showing where the file came from.
 * - Org upload via secure link or email attachment → "Sent by them" (done tone), opens the request
 * - Manual upload by the user → neutral "Your upload"
 */
const SourceBadge = ({ doc, onSarClick }) => {
  const fromSar = doc.source === 'organisation_upload' || (doc.sar_id && doc.source !== 'user_upload');
  if (fromSar && doc.sar_id) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onSarClick(doc.sar_id); }}
        className="rounded-full"
        data-testid={`doc-source-sar-${doc.id}`}
        title="Sent in reply to a request. Opens the request."
      >
        <StatusPill tone="done" size="sm">Sent by them</StatusPill>
      </button>
    );
  }
  return (
    <StatusPill tone="neutral" size="sm" testId={`doc-source-user-${doc.id}`} title="Uploaded by you">
      Your upload
    </StatusPill>
  );
};

// A re-run goes through the whole document again (updating what it found
// before), so a partly-analysed one is "analyse again", never "finish". `analysis_stale`
// (GET /documents) means the pipeline has improved since it last ran.
export const analyseButtonLabel = (doc) => {
  switch (doc.analysis_status) {
    case 'complete':
    case 'partial':
      return 'Analyse again';
    case 'cancelled':
      return doc.analysis_stale ? 'Analyse again' : 'Analyse this document';
    case 'failed':
      return 'Retry analysis';
    default:
      return 'Analyse this document';
  }
};

export const Documents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dragCounterRef = useRef(0);

  const [documents, setDocuments] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingDropFiles, setPendingDropFiles] = useState([]);
  const confirmDialog = useConfirm();
  const [pageDragOver, setPageDragOver] = useState(false);
  const [filterOrgId, setFilterOrgId] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewingDocPage, setViewingDocPage] = useState(null);
  const [analyseDoc, setAnalyseDoc] = useState(null); // single-doc analysis dialog
  const [analyseAllOpen, setAnalyseAllOpen] = useState(false); // bulk analysis dialog
  const [alreadyAnalysedDoc, setAlreadyAnalysedDoc] = useState(null); // "already analysed" explainer
  const [uploadOrgOverride, setUploadOrgOverride] = useState(null); // "upload a newer copy" pre-selects the doc's org

  useEffect(() => {
    // Check for org filter in URL
    const params = new URLSearchParams(location.search);
    const orgParam = params.get('org');
    if (orgParam) {
      setFilterOrgId(orgParam);
    }
  }, [location.search]);

  // Names are decrypted separately (they're sealed under the user's key);
  // merged by id so a reload that lands first isn't overwritten.
  const fillNames = async (docs) => {
    const names = await documentsApi.names(docs.map((d) => d.id));
    setDocuments((prev) => mergeDocumentNames(prev, names));
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [docsRes, orgsRes] = await Promise.all([
          documentsApi.getAll(),
          organisationsApi.getAll()
        ]);
        const docs = docsRes.data.documents || [];
        setDocuments(docs);
        fillNames(docs);
        setOrganisations(orgsRes.data.organisations || []);
        // Deep link from a record's "Open the document" button
        // (/documents?open=<id>&page=<n>) — open the viewer straight away,
        // jumped to the page the record was read from if we know it.
        const searchParams = new URLSearchParams(location.search);
        const openId = searchParams.get('open');
        if (openId) {
          const target = docs.find((d) => d.id === openId);
          if (target) {
            setViewingDoc(target);
            const page = parseInt(searchParams.get('page'), 10);
            setViewingDocPage(Number.isFinite(page) && page > 0 ? page : null);
          }
        }
      } catch (error) {
        toast.error('Your documents could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // Only ever run on mount — re-running when location.search changes would
    // re-open the viewer every time the URL updates for unrelated reasons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters: organisation, document type, source
  const filteredDocuments = documents.filter((d) => {
    if (filterOrgId && d.organisation_id !== filterOrgId) return false;
    if (filterDocType && d.document_type !== filterDocType) return false;
    if (filterSource) {
      const fromSar = d.source === 'organisation_upload' || (d.sar_id && d.source !== 'user_upload');
      if (filterSource === 'sar' && !fromSar) return false;
      if (filterSource === 'user' && fromSar) return false;
    }
    return true;
  });

  const hasActiveFilters = !!(filterOrgId || filterDocType || filterSource);

  // Show only doc-type / org options that exist in the user's data, so empty
  // categories don't clutter the dropdowns.
  const availableDocTypes = DOCUMENT_TYPES.filter(t =>
    documents.some(d => d.document_type === t.id)
  );
  const availableOrgs = organisations.filter(o =>
    documents.some(d => d.organisation_id === o.id)
  );

  const clearFilters = () => {
    setFilterOrgId('');
    setFilterDocType('');
    setFilterSource('');
    navigate('/documents');
  };


  const reloadDocuments = async () => {
    try {
      const docsRes = await documentsApi.getAll();
      const docs = docsRes.data.documents || [];
      setDocuments(docs);
      fillNames(docs);
    } catch {
      // toast already shown elsewhere
    }
  };

  const dismissRead = async (docId) => {
    setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, read: true } : d)));
    try {
      await documentsApi.dismissRead(docId);
    } catch {
      toast.error('That could not be marked as read. Try again.');
      reloadDocuments();
    }
  };

  // Page-level drag-drop. Handlers are bound to the <main> element below.
  // We keep a ref counter so nested elements don't make the overlay flicker on dragenter/leave.
  const onPageDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setPageDragOver(true);
  };

  const onPageDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
  };

  const onPageDragLeave = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setPageDragOver(false);
  };

  const onPageDrop = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setPageDragOver(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (!dropped.length) return;
    if (organisations.length === 0) {
      toast.error('Add an organisation before uploading documents.');
      return;
    }
    setPendingDropFiles(dropped);
    setUploadOpen(true);
  };

  const handleDelete = async (doc) => {
    const analysed = doc.analysis_status && doc.analysis_status !== 'not_started';
    try {
      const ok = await confirmDialog({
        title: 'Delete this document?',
        detail: doc.original_filename,
        description: analysed
          ? 'The file is removed for good, along with the records analysis made from it and any notes or highlights on those records. This cannot be undone.'
          : 'The file is removed for good. This cannot be undone.',
        confirmLabel: 'Delete',
        cancelLabel: 'Keep it',
        destructive: true,
        action: () => documentsApi.delete(doc.id),
      });
      if (!ok) return;
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success('Document deleted.');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'The document could not be deleted.';
      toast.error(msg);
    }
  };

  const getOrgName = (orgId) => {
    const org = organisations.find(o => o.id === orgId);
    return org?.name || 'Unknown';
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <div className="animate-pulse text-ink-55">Loading</div>
      </div>
    );
  }

  return (
    <AppShell>

      <main
        className="relative"
        onDragEnter={onPageDragEnter}
        onDragOver={onPageDragOver}
        onDragLeave={onPageDragLeave}
        onDrop={onPageDrop}
      >
        {/* Drop overlay (only visible while a file is being dragged anywhere on the page) */}
        {pageDragOver && (
          <div
            className="fixed inset-0 z-40 bg-ink/20 flex items-center justify-center pointer-events-none"
            data-testid="page-drop-overlay"
          >
            <div className="bg-surface border-2 border-dashed border-brand rounded-xl p-10 text-center shadow-overlay">
              <p className="font-heading font-semibold text-[22px] tracking-[-0.02em]">Drop your file to upload</p>
              <p className="text-sm text-ink-55 mt-1">It is encrypted before it is stored.</p>
            </div>
          </div>
        )}

        <PageHeader
          title="Documents"
          lede="Files you have uploaded and files organisations have sent. Only your key can open them."
          actions={(
            <>
              {(() => {
                const unanalysedCount = documents.filter((d) => {
                  const s = d.analysis_status;
                  return !s || s === 'not_started' || s === 'failed' || s === 'partial';
                }).length;
                return unanalysedCount > 0 ? (
                  <Button
                    onClick={() => setAnalyseAllOpen(true)}
                    variant="secondary"
                    data-testid="analyse-all-btn"
                  >
                    Analyse all ({unanalysedCount})
                  </Button>
                ) : null;
              })()}
              <Button
                onClick={() => { setPendingDropFile(null); setUploadOpen(true); }}
                disabled={organisations.length === 0}
                className="flex-shrink-0"
                data-testid="open-upload-dialog-btn"
              >
                Upload a document
              </Button>
            </>
          )}
        />

        {/* Filters bar */}
        {documents.length > 0 && (
          <div className="rounded-lg bg-ground p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="pd-eyebrow flex-shrink-0">Filter</span>

              <div className="flex flex-col sm:flex-row gap-2 flex-1">
                {/* Organisation filter */}
                <Select value={filterOrgId || 'all'} onValueChange={(v) => setFilterOrgId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="bg-surface border-surface sm:max-w-[260px]" data-testid="filter-org-trigger">
                    <SelectValue placeholder="All organisations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All organisations</SelectItem>
                    {availableOrgs.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Document type filter */}
                <Select value={filterDocType || 'all'} onValueChange={(v) => setFilterDocType(v === 'all' ? '' : v)}>
                  <SelectTrigger className="bg-surface border-surface sm:max-w-[220px]" data-testid="filter-doctype-trigger">
                    <SelectValue placeholder="All document types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All document types</SelectItem>
                    {availableDocTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Source filter */}
                <Select value={filterSource || 'all'} onValueChange={(v) => setFilterSource(v === 'all' ? '' : v)}>
                  <SelectTrigger className="bg-surface border-surface sm:max-w-[180px]" data-testid="filter-source-trigger">
                    <SelectValue placeholder="Any source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any source</SelectItem>
                    <SelectItem value="sar">Sent by an organisation</SelectItem>
                    <SelectItem value="user">Your uploads</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-sm text-ink-55" data-testid="filter-count">
                  {filteredDocuments.length} of {documents.length}
                </span>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    data-testid="clear-filters-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {filteredDocuments.length === 0 ? (
          <div className="rounded-lg bg-ground py-14 px-6 text-center">
            <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mx-auto mb-5" />
            <h3 className="font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink mb-2">
              {hasActiveFilters ? 'Nothing matches these filters' : 'Nothing here yet'}
            </h3>
            <p className="text-base text-ink-55 mb-6 max-w-md mx-auto">
              {hasActiveFilters
                ? 'Clear the filters to see all your documents.'
                : 'Documents appear here when an organisation replies to a request, or when you upload a file yourself.'}
            </p>
            {hasActiveFilters ? (
              <Button onClick={clearFilters} variant="secondary">
                Clear filters
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => { setPendingDropFile(null); setUploadOpen(true); }}
                disabled={organisations.length === 0}
                data-testid="empty-upload-btn"
              >
                Upload a document
              </Button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredDocuments.map((doc) => {
              const docTypeLabel = DOCUMENT_TYPES.find(t => t.id === doc.document_type)?.label || doc.document_type;
              const fromSar = doc.source === 'organisation_upload' || (doc.sar_id && doc.source !== 'user_upload');

              return (
                <div
                  key={doc.id}
                  className="rounded-lg bg-ground p-5"
                  data-testid={`doc-card-${doc.id}`}
                >
                  <p className="pd-mono text-ink-55 mb-2">
                    {formatDate(doc.uploaded_at)} · {docTypeLabel}
                  </p>

                  <button
                    type="button"
                    onClick={() => { setViewingDoc(doc); setViewingDocPage(null); }}
                    className="text-left w-full rounded-md"
                  >
                    <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink mb-1 truncate hover:text-brand transition-colors duration-state">
                      {doc.original_filename}
                    </h3>
                  </button>
                  <p className="text-sm text-ink-55 mb-3">
                    {getOrgName(doc.organisation_id)} · {formatFileSize(doc.file_size)}
                  </p>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <SourceBadge doc={doc} onSarClick={(sarId) => navigate(`/sar/${sarId}`)} />
                    {!doc.read && (
                      <StatusPill size="sm" tone="needs" testId={`doc-unread-${doc.id}`} title="You haven't opened this one yet">
                        Unread
                      </StatusPill>
                    )}
                    {doc.analysis_status && doc.analysis_status !== 'not_started' && (
                      <StatusPill
                        size="sm"
                        tone={
                          doc.analysis_status === 'complete' ? 'done'
                          : doc.analysis_status === 'partial' ? 'needs'
                          : doc.analysis_status === 'failed' ? 'breach'
                          : doc.analysis_status === 'cancelled' ? 'neutral'
                          : 'held'
                        }
                        testId={`analysis-status-${doc.id}`}
                      >
                        {doc.analysis_status === 'complete' ? 'Analysed'
                        : doc.analysis_status === 'analysing' ? 'Analysing'
                        : doc.analysis_status === 'queued' ? 'Queued'
                        : doc.analysis_status === 'partial' ? 'Partly analysed'
                        : doc.analysis_status === 'cancelled' ? 'Cancelled'
                        : 'Could not analyse'}
                      </StatusPill>
                    )}
                  </div>

                  {!doc.read && (
                    <button
                      type="button"
                      onClick={() => dismissRead(doc.id)}
                      className="mt-1.5 text-sm text-brand hover:text-brand-ink font-medium"
                      data-testid={`dismiss-read-${doc.id}`}
                    >
                      Mark as read without opening
                    </button>
                  )}

                  <div className="mt-3 pt-3 border-t border-rule flex items-center justify-between gap-2">
                    {doc.analysis_status === 'queued' || doc.analysis_status === 'analysing' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled
                        className="bg-brand-wash text-brand-ink animate-pulse"
                        data-testid={`analyse-doc-${doc.id}`}
                      >
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />
                        {doc.analysis_status === 'queued' ? 'Queued' : 'Analysing…'}
                      </Button>
                    ) : doc.analysis_status === 'complete' && !doc.analysis_stale ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAlreadyAnalysedDoc(doc)}
                        className="text-ink-55 hover:text-ink-70"
                        data-testid={`analyse-doc-${doc.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                        Analysed
                      </Button>
                    ) : (
                      <div className="min-w-0">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setAnalyseDoc(doc)}
                          data-testid={`analyse-doc-${doc.id}`}
                        >
                          {analyseButtonLabel(doc)}
                        </Button>
                        {doc.analysis_stale && (
                          <p className="text-sm text-ink-55 mt-1" data-testid={`analysis-improved-${doc.id}`}>
                            Our analysis has improved since this ran.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setViewingDoc(doc); setViewingDocPage(null); }}
                        className="h-9 w-9"
                        data-testid={`view-doc-${doc.id}`}
                        aria-label="Open this document"
                        title="Open"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {fromSar ? (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled
                                  className="h-9 w-9 cursor-not-allowed"
                                  data-testid={`delete-doc-disabled-${doc.id}`}
                                  aria-label="Cannot delete. This file is part of a reply to a request"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                This file is part of an organisation's reply and cannot be deleted here.
                                It stays on the request as evidence.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(doc)}
                          className="h-9 w-9 hover:text-breach-ink"
                          data-testid={`delete-doc-${doc.id}`}
                          aria-label="Delete this document"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <DocumentViewer
        doc={viewingDoc}
        open={!!viewingDoc}
        initialPage={viewingDocPage}
        onOpenChange={(open) => { if (!open) { setViewingDoc(null); setViewingDocPage(null); } }}
        onViewed={(docId) => setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, read: true } : d)))}
      />

      <UploadDocumentDialog
        open={uploadOpen}
        onOpenChange={(o) => { setUploadOpen(o); if (!o) setUploadOrgOverride(null); }}
        organisations={organisations}
        defaultOrgId={uploadOrgOverride || filterOrgId}
        prefilledFiles={pendingDropFiles}
        onUploaded={reloadDocuments}
      />


      <AnalyseDocumentDialog
        open={!!analyseDoc}
        onOpenChange={(o) => { if (!o) setAnalyseDoc(null); }}
        doc={analyseDoc}
        onDone={reloadDocuments}
      />

      <AnalyseDocumentDialog
        open={analyseAllOpen}
        onOpenChange={setAnalyseAllOpen}
        totalUnanalysed={documents.filter((d) => {
          const s = d.analysis_status;
          return !s || s === 'not_started' || s === 'failed' || s === 'partial';
        }).length}
        onDone={reloadDocuments}
      />

      <AlreadyAnalysedDialog
        doc={alreadyAnalysedDoc}
        open={!!alreadyAnalysedDoc}
        onOpenChange={(o) => { if (!o) setAlreadyAnalysedDoc(null); }}
        onReanalysed={reloadDocuments}
        onUploadNewer={(doc) => {
          setUploadOrgOverride(doc.organisation_id);
          setPendingDropFiles([]);
          setUploadOpen(true);
        }}
      />
    </AppShell>
  );
};

export default Documents;
